import { memo, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, Megaphone, ArrowRight, Gamepad2, Briefcase, Send, Sparkles, Play, ChevronRight, Music, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Layout from '@/components/layout/Layout';
import NewsCarousel from '@/components/news/NewsCarousel';
import MusicaDaSorteCard from '@/components/musica-da-sorte/CardHome';
import FeaturedBands from '@/components/home/FeaturedBands';
import MarqueeTicker from '@/components/home/MarqueeTicker';
import UpcomingEvents from '@/components/home/UpcomingEvents';
import ScrollProgress from '@/components/common/ScrollProgress';
import { useSiteConfig, type MusicaDoDia } from '@/hooks/useSiteConfig';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import logoEssencia from '/lovable-uploads/e103bf0a-04c5-4266-926c-c5c3c3d46f35.png';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

import { useScrollReveal } from '@/hooks/useScrollReveal';
import RevealBlock from '@/components/common/RevealBlock';

/* ── YouTube IFrame API loader (singleton) ──────────────────── */
let ytApiReady = false;
let ytApiPromise: Promise<void> | null = null;

const loadYTApi = (): Promise<void> => {
  if (ytApiReady) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise<void>((resolve) => {
    if (typeof window !== 'undefined' && (window as any).YT?.Player) {
      ytApiReady = true;
      resolve();
      return;
    }
    (window as any).onYouTubeIframeAPIReady = () => {
      ytApiReady = true;
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

/* ── Música do Dia Player ─────────────────────────────────── */
const MusicaDoDiaPlayer = memo(({ musica }: { musica: MusicaDoDia }) => {
  const [playing, setPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [thumbSrc, setThumbSrc] = useState(() => `https://img.youtube.com/vi/${musica.youtube_id}/maxresdefault.jpg`);

  useEffect(() => {
    setThumbSrc(`https://img.youtube.com/vi/${musica.youtube_id}/maxresdefault.jpg`);
  }, [musica.youtube_id]);

  const thumbnailUrl = thumbSrc;

  useEffect(() => { loadYTApi(); }, []);

  const handlePlay = useCallback(async () => {
    if (playing) return;
    setPlaying(true);
    setVideoReady(false);
    await loadYTApi();
    setTimeout(() => {
      if (playerContainerRef.current && !playerRef.current) {
        let readyTimeout: ReturnType<typeof setTimeout> | null = null;
        playerRef.current = new (window as any).YT.Player(playerContainerRef.current, {
          videoId: musica.youtube_id, width: '100%', height: '100%',
          playerVars: { autoplay: 1, rel: 0, modestbranding: 1, playsinline: 1 },
          events: {
            onReady: (e: any) => {
              e.target.playVideo();
              // Fallback: se o evento PLAYING não disparar em 4s, revela o vídeo mesmo assim
              readyTimeout = setTimeout(() => setVideoReady(true), 4000);
            },
            onStateChange: (e: any) => {
              if (e.data === (window as any).YT.PlayerState.PLAYING) {
                if (readyTimeout) clearTimeout(readyTimeout);
                setVideoReady(true);
              }
            },
            onError: () => { if (readyTimeout) clearTimeout(readyTimeout); setPlaying(false); setVideoReady(false); },
          },
        });
      }
    }, 50);
  }, [musica.youtube_id, playing]);

  useEffect(() => {
    return () => { if (playerRef.current?.destroy) { try { playerRef.current.destroy(); } catch {} } playerRef.current = null; };
  }, []);

  return (
    <div className="hero-musica w-full max-w-[560px] lg:max-w-[600px] mx-auto px-4">
      <div className="relative rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur-2xl p-5 sm:p-6 shadow-2xl">
        <span className="hero-accent-bg absolute -top-3 right-6 text-[10px] font-black uppercase tracking-[0.2em] px-4 py-1 rounded-full shadow-lg">
          Música do Dia
        </span>

        <div
          className="musica-dia-card relative w-full aspect-video rounded-2xl overflow-hidden cursor-pointer group bg-black"
          onClick={!playing ? handlePlay : undefined}
          role={!playing ? 'button' : undefined}
          tabIndex={!playing ? 0 : undefined}
          onKeyDown={(e) => { if (!playing && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handlePlay(); } }}
          aria-label={!playing ? `Reproduzir ${musica.nome} — ${musica.banda}` : undefined}
        >
          <div className="absolute inset-0 z-10 transition-opacity duration-500" style={{ opacity: playing && videoReady ? 0 : 1, pointerEvents: playing && videoReady ? 'none' : 'auto' }}>
            <img
              src={thumbnailUrl}
              alt={`${musica.nome} - ${musica.banda}`}
              className="musica-dia-cover w-full h-full object-cover"
              loading="eager"
              onLoad={(e) => {
                // YouTube retorna um placeholder 120x90 (HTTP 200) quando maxresdefault não existe
                if (e.currentTarget.naturalWidth < 200) {
                  const fallback = `https://img.youtube.com/vi/${musica.youtube_id}/hqdefault.jpg`;
                  if (thumbSrc !== fallback) setThumbSrc(fallback);
                }
              }}
              onError={() => {
                const fallback = `https://img.youtube.com/vi/${musica.youtube_id}/hqdefault.jpg`;
                if (thumbSrc !== fallback) setThumbSrc(fallback);
              }}
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-colors duration-500 group-hover:bg-black/25">
              {playing && !videoReady ? (
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
              ) : !playing ? (
                <div className="musica-dia-play w-16 h-16 md:w-[72px] md:h-[72px] rounded-full flex items-center justify-center">
                  <Play size={26} className="text-white ml-[3px]" fill="currentColor" />
                </div>
              ) : null}
            </div>
          </div>
          <div ref={playerContainerRef} className="absolute inset-0 w-full h-full" style={{ visibility: playing ? 'visible' : 'hidden' }} />
        </div>

        <div className="mt-5 flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-bold text-lg sm:text-xl tracking-tight truncate">{musica.nome}</h3>
            <p className="hero-accent-text text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] truncate mt-1">{musica.banda}</p>
          </div>
          {playing && videoReady && (
            <div className="waveform-bars waveform-bars--sm shrink-0" aria-hidden="true"><span /><span /><span /></div>
          )}
        </div>
      </div>
    </div>
  );


});
MusicaDoDiaPlayer.displayName = 'MusicaDoDiaPlayer';

/* ── Animated Counter ─────────────────────────────────────── */
const AnimatedCounter = ({ end, suffix = '' }: { end: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.3, freezeOnceVisible: true });

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isVisible, end]);

  return <span ref={ref}>{count.toLocaleString('pt-BR')}{suffix}</span>;
};


/* ── Hero Section ─────────────────────────────────────────── */
const HeroSection = memo(({ musicaDoDia }: { musicaDoDia: MusicaDoDia | null }) => {
  const hasMusicaDoDia = !!musicaDoDia;
  const [introComplete, setIntroComplete] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setIntroComplete(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Parallax sutil — rAF, sem re-render, desligado com prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const apply = () => {
      raf = 0;
      const el = bgRef.current;
      if (el) el.style.transform = `translate3d(0, ${window.scrollY * 0.12}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(apply); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const step = (order: number, distance = 14) => ({
    opacity: introComplete ? 1 : 0,
    transform: introComplete ? 'translate3d(0,0,0)' : `translate3d(0,${distance}px,0)`,
    transition: `opacity 0.7s ease-out ${order * 0.12}s, transform 0.7s ease-out ${order * 0.12}s`,
  });

  return (
    <section ref={heroRef} className="hero-shell relative min-h-screen flex items-stretch overflow-hidden">
      <picture>
        <source media="(max-width: 640px)" srcSet="/hero-mobile.jpg" />
        <source media="(min-width: 641px)" srcSet="/hero-stage-fire.jpg" />
        <img
          ref={bgRef}
          src="/hero-stage-fire.jpg"
          alt=""
          aria-hidden="true"
          className="hero-bg absolute inset-0 w-full h-full object-cover scale-110 will-change-transform"
          loading="eager"
          {...{ fetchpriority: "high" }}
        />
      </picture>
      <div className="hero-veil absolute inset-0 z-[2]" />

      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-16 px-5 lg:px-10 py-24 lg:py-0">
        {/* Marca */}
        <div className="w-full lg:flex-1 flex flex-col items-center lg:items-start justify-center text-center lg:text-left">
          <img
            src={logoEssencia}
            alt="Essência das Bandas"
            className="h-20 sm:h-28 lg:h-32 w-auto"
            width={320}
            height={160}
            loading="eager"
            {...{ fetchpriority: "high" }}
            style={{ ...step(0, 10), filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.55))' }}
          />

          <div className="hidden lg:flex items-center gap-4 mt-6" style={step(1)}>
            <span className="hero-accent-rule h-px w-12" aria-hidden="true" />
            <p className="text-[11px] font-bold uppercase tracking-[0.42em] text-white/80">
              Conectamos o Sul através da música
            </p>
          </div>

          <h1
            className="hero-welcome font-signature text-white mt-6 lg:mt-8 max-w-[16ch]"
            style={step(2)}
          >
            Sejam Bem-Vindos!
          </h1>
        </div>



        {/* Música do Dia */}
        {hasMusicaDoDia && (
          <div className="w-full lg:flex-1 flex items-center justify-center" style={step(2, 20)}>
            <MusicaDoDiaPlayer musica={musicaDoDia!} />
          </div>
        )}

        {/* Atalhos — apenas mobile */}
        <div className="sm:hidden w-full max-w-xs mx-auto flex flex-col gap-2" style={step(3, 18)}>
          <Link to="/bandas" className="mobile-premium-btn"><Users size={16} />Bandas</Link>
          <Link to="/cadastre-evento" className="mobile-premium-btn"><Calendar size={16} />Cadastre seu Evento</Link>
          <Link to="/agenda-eventos" className="mobile-premium-btn"><Calendar size={16} />Agenda de Eventos</Link>
          <Link to="/noticias" className="mobile-premium-btn"><Megaphone size={16} />Notícias</Link>
          <Link to="/quiz" className="mobile-premium-btn"><Briefcase size={16} />Quiz</Link>
          <Link to="/jogo-memoria" className="mobile-premium-btn"><Gamepad2 size={16} />Jogo da Memória</Link>
          <Link to="/contato" className="mobile-premium-btn"><Users size={16} />Contato</Link>
        </div>
      </div>
    </section>
  );
});

HeroSection.displayName = 'HeroSection';

/* ── Spotify Section (Lazy iframe) ───────────────────────── */
const SpotifySection = memo(({ spotifyPlaylist }: { spotifyPlaylist: { embed_url: string } }) => {
  const [spotifyRef, spotifyInView] = useIntersectionObserver({ rootMargin: '200px', freezeOnceVisible: true });

  return (
    <section className="py-20 md:py-28 sobre-mission-bg relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="sobre-orb sobre-orb-2" />
      </div>
      <div className="container mx-auto px-4 text-center relative z-10" ref={spotifyRef}>
        <RevealBlock>
          <p className="text-sm tracking-[0.25em] uppercase text-white/40 mb-4 font-medium">Playlist Oficial</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            <span className="text-white">Ouça </span><span className="sobre-gradient-text">Agora</span>
          </h2>
          <p className="text-lg text-white/50 mb-12 max-w-2xl mx-auto">
            Descubra os maiores sucessos do bailão em nossa playlist oficial.
          </p>
        </RevealBlock>
        <RevealBlock delay={200}>
          <div className="max-w-2xl lg:max-w-4xl mx-auto">
            <div className="sobre-glass-card rounded-xl p-4 sm:p-8">
              <div className="aspect-video max-w-full overflow-hidden rounded-lg">
                {spotifyInView ? (
                  <iframe src={spotifyPlaylist.embed_url} width="100%" height="352" frameBorder="0" allowFullScreen allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="w-full h-full min-h-[300px] sm:min-h-[352px]" title="Spotify Playlist" />
                ) : (
                  <div className="w-full h-full min-h-[300px] sm:min-h-[352px] bg-white/[0.03] rounded-lg flex items-center justify-center">
                    <Music className="w-8 h-8 text-white/20 animate-pulse" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </RevealBlock>
      </div>
    </section>
  );
});
SpotifySection.displayName = 'SpotifySection';

/* ── Suggestion Section (with noise texture) ─────────────── */
const SuggestionSection = memo(() => {
  const [sugestao, setSugestao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const handleSubmit = async () => {
    if (!sugestao.trim()) return;
    setEnviando(true);
    try {
      const { error } = await supabase.from('sugestoes' as any).insert({ tipo: 'geral', origem: 'home', mensagem: sugestao.trim() } as any);
      if (error) throw error;
      toast.success("Sua sugestão foi encaminhada ao administrador do site. Obrigado!");
      setSugestao('');
    } catch {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      {/* Noise texture background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, hsl(221,89%,8%) 0%, hsl(280,59%,12%) 50%, hsl(221,89%,6%) 100%)' }} />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="sobre-orb sobre-orb-3" />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          <RevealBlock>
            <div className="sobre-glass-card rounded-2xl p-8 md:p-12 text-center">
              <div className="inline-flex p-3 rounded-xl bg-white/5 mb-6">
                <Sparkles className="h-6 w-6 text-white/50" strokeWidth={1.5} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
                <span className="text-white">O que você </span><span className="sobre-gradient-text">gostaria de ver?</span>
              </h2>
              <p className="text-white/50 mb-8 text-base">
                Sua opinião é muito importante! Sugira bandas, quadros, melhorias ou qualquer ideia.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Digite sua sugestão..."
                  value={sugestao}
                  onChange={e => setSugestao(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/25 flex-1 focus:border-white/20 h-12"
                />
                <button
                  onClick={handleSubmit}
                  disabled={enviando || !sugestao.trim()}
                  className="sobre-cta-button text-white px-6 h-12 rounded-md font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                  Enviar
                </button>
              </div>
            </div>
          </RevealBlock>
        </div>
      </div>
    </section>
  );
});
SuggestionSection.displayName = 'SuggestionSection';

/* ── CTA "Leia todas as notícias" ────────────────────────── */
const NewsCTA = memo(() => (
  <section className="py-10 bg-black relative">
    <div className="absolute inset-0 bg-gradient-to-r from-[hsl(221,89%,6%)] via-[hsl(280,59%,8%)] to-[hsl(221,89%,6%)]" />
    <div className="container mx-auto px-4 text-center relative z-10">
      <RevealBlock>
        <Link to="/noticias">
          <button className="sobre-cta-button text-white group px-8 py-4 rounded-xl font-medium text-base inline-flex items-center gap-3 hover:opacity-90 transition-all duration-300 hover:scale-[1.02]">
            Leia todas as notícias
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </RevealBlock>
    </div>
  </section>
));
NewsCTA.displayName = 'NewsCTA';

/* ── Main Page ───────────────────────────────────────────── */
const Index = () => {
  const { spotifyPlaylist, musicaDoDia, isLoading: isLoadingConfig } = useSiteConfig();

  return (
    <Layout>
      <ScrollProgress />
      <HeroSection musicaDoDia={musicaDoDia} />
      <MarqueeTicker />
      <MusicaDaSorteCard />
      <NewsCarousel />
      <NewsCTA />
      
      <FeaturedBands />
      <SuggestionSection />
      <UpcomingEvents />
      {!isLoadingConfig && spotifyPlaylist && <SpotifySection spotifyPlaylist={spotifyPlaylist} />}
    </Layout>
  );
};
export default Index;
